import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowUpRight, X } from "lucide-react";
import { useI18n } from "../../i18n";

// Exact coordinates of La Caletta all'Alba (Via Cala Luna, La Caletta — Siniscola, NU)
const LAT = 40.60937192968102;
const LNG = 9.74257284280859;
const ZOOM = 16;
// Hide Google's default red pin by using a generic map view at the same center,
// then overlay our editorial marker on top.
const EMBED_URL = `https://www.google.com/maps?q=${LAT},${LNG}&hl=it&z=${ZOOM}&output=embed`;
const NAV_URL = `https://www.google.com/maps/dir/?api=1&destination=${LAT},${LNG}&destination_place_id=La+Caletta+all%27Alba`;
const OPEN_URL = `https://www.google.com/maps/search/?api=1&query=${LAT},${LNG}&query_place_id=La+Caletta+all%27Alba`;

export default function MapSection() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const labels = lang === "it"
    ? { open: "Apri in Google Maps", nav: "Indicazioni stradali", view: "Vista mare · Casa vacanze" }
    : { open: "Open in Google Maps", nav: "Get directions", view: "Sea view · Holiday home" };

  return (
    <section id="location" className="relative bg-sand-50 py-24 md:py-36 lg:py-44" data-testid="map-section">
      <div className="px-6 md:px-12 lg:px-20 max-w-7xl mx-auto">
        <div className="grid grid-cols-12 gap-y-12 gap-x-8 lg:gap-x-16 items-center">
          <div className="col-span-12 md:col-span-4">
            <span className="eyebrow">{t.map.eyebrow}</span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-charcoal mt-4 whitespace-pre-line"
              data-testid="map-title"
            >
              {t.map.title}
            </motion.h2>
            <p className="mt-7 text-charcoal-soft text-base md:text-lg leading-relaxed">
              {t.map.body}
            </p>
            <div className="mt-8 flex items-center gap-3 text-charcoal text-sm">
              <MapPin size={16} strokeWidth={1.3} />
              <span className="tracking-wide">Via Cala Luna · La Caletta · Sardegna</span>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.2 }}
            className="col-span-12 md:col-span-8"
          >
            <div
              className="relative overflow-hidden aspect-[16/11] md:aspect-[4/3] border border-charcoal/10 bg-sand-100"
              data-testid="map-container"
            >
              <iframe
                title="La Caletta — Siniscola"
                src={EMBED_URL}
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(0.6) contrast(0.95)" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="map-iframe"
              />

              {/* Editorial custom marker — centered over the iframe */}
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label="La Caletta all'Alba"
                data-testid="map-marker"
                className="group absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full z-20 flex flex-col items-center focus:outline-none"
              >
                <span className="relative flex items-center justify-center">
                  <span className="absolute w-14 h-14 rounded-full bg-charcoal/15 animate-ping" />
                  <span className="relative flex items-center justify-center w-11 h-11 rounded-full bg-charcoal text-ivory shadow-[0_8px_22px_rgba(31,31,29,0.35)] ring-4 ring-ivory/90 transition-transform duration-500 group-hover:scale-110">
                    <MapPin size={16} strokeWidth={1.4} />
                  </span>
                </span>
                <span className="mt-2 px-3 py-1 bg-charcoal text-ivory text-[0.6rem] tracking-luxe uppercase whitespace-nowrap shadow-[0_6px_18px_rgba(31,31,29,0.25)]">
                  La Caletta all'Alba
                </span>
                <span className="block w-px h-3 bg-charcoal/70 mt-0.5" />
              </button>

              {/* Popup card revealed on marker click */}
              <AnimatePresence>
                {open && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute left-1/2 -translate-x-1/2 z-30 bottom-6 w-[min(360px,calc(100%-2rem))] bg-ivory border border-charcoal/10 shadow-[0_20px_50px_-20px_rgba(31,31,29,0.35)] p-5"
                    data-testid="map-popup"
                  >
                    <button
                      onClick={() => setOpen(false)}
                      className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-charcoal/60 hover:text-charcoal transition"
                      aria-label="Close"
                      data-testid="map-popup-close"
                    >
                      <X size={14} strokeWidth={1.4} />
                    </button>
                    <span className="eyebrow">Casa vacanze</span>
                    <h3 className="font-serif text-2xl text-charcoal mt-2 leading-tight">
                      La Caletta all'Alba
                    </h3>
                    <p className="mt-1 text-xs tracking-wide text-charcoal-mute">
                      Via Cala Luna · La Caletta · Sardegna
                    </p>
                    <p className="mt-3 text-sm text-charcoal-soft">{labels.view}</p>
                    <div className="mt-5 flex flex-col gap-2">
                      <a
                        href={NAV_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-between gap-2 bg-charcoal text-ivory px-4 py-3 text-[0.65rem] tracking-luxe uppercase hover:bg-charcoal-soft transition-colors duration-500"
                        data-testid="map-directions-cta"
                      >
                        <span>{labels.nav}</span>
                        <ArrowUpRight size={14} strokeWidth={1.4} />
                      </a>
                      <a
                        href={OPEN_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-between gap-2 border border-charcoal/20 text-charcoal px-4 py-3 text-[0.65rem] tracking-luxe uppercase hover:border-charcoal hover:bg-charcoal hover:text-ivory transition-all duration-500"
                        data-testid="map-open-cta"
                      >
                        <span>{labels.open}</span>
                        <ArrowUpRight size={14} strokeWidth={1.4} />
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="pointer-events-none absolute inset-0 ring-1 ring-charcoal/5" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
